import { FeatureFlag } from '@caresend/types';
import { getEligibleTimeSlots, getArrayOfAllTimeSlots, convertCentsToDollar, getBestEligibleNurse, objectFilter } from '@caresend/utils';
import { getStore } from '../../../helpers/initStore.js';
import { flagOn } from '../../variables/index.js';

/**
 * Gets the eligible procedure IDs for the scheduling flow.
 *
 * If we are rescheduling, we need to filter out the procedures that are
 * ineligible for rescheduling. These procedures have been canceled or
 * failed by the demand side (patient, prescriber, etc).
 */
const getEligibleProcedureIDs = (procedures, proceduresIneligibleForRescheduling) => {
    const store = getStore();
    const { localState } = store.state.schedulingFlow;
    const allProcedureIDs = Object.keys(procedures);
    if (!localState.isRescheduling)
        return allProcedureIDs;
    const eligibleProcedureIDs = allProcedureIDs.filter((procedureID) => !(proceduresIneligibleForRescheduling === null || proceduresIneligibleForRescheduling === void 0 ? void 0 : proceduresIneligibleForRescheduling.includes(procedureID)));
    return eligibleProcedureIDs;
};
const getOfficeToNursesMap = (timeSlot) => {
    var _a;
    const store = getStore();
    const { adminModeAllStaffingOffices = [] } = store.state.schedulingFlow;
    const initialOfficeToNursesMap = adminModeAllStaffingOffices.reduce((acc, office) => ({ ...acc, [office.id]: [] }), {});
    return Object.values((_a = timeSlot.availableNurses) !== null && _a !== void 0 ? _a : {}).reduce((officeToNursesMap, nurse) => {
        var _a;
        const { staffingOfficeID } = nurse;
        if (!staffingOfficeID || !officeToNursesMap[staffingOfficeID])
            return officeToNursesMap;
        if (staffingOfficeID)
            (_a = officeToNursesMap[staffingOfficeID]) === null || _a === void 0 ? void 0 : _a.push(nurse);
        return officeToNursesMap;
    }, initialOfficeToNursesMap);
};
const getPatientPays = (office) => { var _a; return !!((_a = office.bookingPricing) === null || _a === void 0 ? void 0 : _a.paymentEnabled); };
const isSchedulingDryRun = (draftWaypoint) => {
    var _a;
    const isDryRunDueToFeatureFlag = flagOn(FeatureFlag.SCHEDULING_FLOW_DRY_RUN);
    const isDryRunDueToDebugProperty = !!((_a = draftWaypoint.debug) === null || _a === void 0 ? void 0 : _a.dryRun);
    return isDryRunDueToFeatureFlag || isDryRunDueToDebugProperty;
};
const getTextWithDryRun = (title, { store }) => {
    const isDryRun = store.state.schedulingFlow.draftWaypoint
        ? isSchedulingDryRun(store.state.schedulingFlow.draftWaypoint)
        : false;
    if (!isDryRun)
        return title;
    return title ? `${title} (dry run)` : 'Dry run enabled';
};
/**
 * Get the staffing info for each office per time slot.
 *
 * This is used to display the staffing info in the admin mode filters.
 *
 * @param timeSlots - The time slots to get the staffing info from.
 * @returns The staffing info per time slot.
 */
const getStaffingInfoPerTimeSlot = (timeSlots) => {
    const store = getStore();
    const { adminModeAllStaffingOffices = [] } = store.state.schedulingFlow;
    const staffingInfoByTimeSlot = {};
    const eligibleTimeSlots = getEligibleTimeSlots(timeSlots);
    const timeSlotArray = getArrayOfAllTimeSlots(eligibleTimeSlots);
    /**
     * Iterate over each time slot and get the staffing info (per office)
     * based off of the nurses on the timeslot that are associated with each
     * respecitive office.
     */
    timeSlotArray.forEach((timeSlot) => {
        if (timeSlot.disabled)
            return;
        const nursesByOffice = getOfficeToNursesMap(timeSlot);
        Object.entries(nursesByOffice).forEach(([officeID, nurses]) => {
            var _a, _b, _c, _d;
            const officeName = (_a = adminModeAllStaffingOffices.find((office) => office.id === officeID)) === null || _a === void 0 ? void 0 : _a.name;
            if (!officeName)
                return;
            if (nurses.length === 0)
                return;
            const startTime = timeSlot.startTime.toString();
            const sortedLaborCosts = nurses
                .map((nurse) => nurse.laborCost)
                .sort((a, b) => a - b);
            const cheapest = convertCentsToDollar((_b = sortedLaborCosts[0]) !== null && _b !== void 0 ? _b : 0);
            const mostExpensive = convertCentsToDollar((_c = sortedLaborCosts.at(-1)) !== null && _c !== void 0 ? _c : 0);
            const priceRange = sortedLaborCosts.length === 1
                ? [cheapest]
                : [cheapest, mostExpensive];
            if (!staffingInfoByTimeSlot[startTime])
                staffingInfoByTimeSlot[startTime] = [];
            (_d = staffingInfoByTimeSlot[startTime]) === null || _d === void 0 ? void 0 : _d.push({
                officeName,
                officeID,
                nurseCount: nurses.length,
                priceRange,
            });
        });
    });
    return staffingInfoByTimeSlot;
};
/**
 * Gets a time slot with some updated fields when confirming a mobile visit.
 *
 * Currently, this is used to update the time slot with the preferred offices
 * from the admin mode office filters and the best ranked nurse.
 */
const getUpdatedTimeSlot = (timeSlot) => {
    const store = getStore();
    const { adminMode, adminModeOfficeFilters = [], } = store.state.schedulingFlow;
    if (!adminMode)
        return timeSlot;
    const preferredOffices = adminModeOfficeFilters.map((office) => office.id);
    const bestNurse = getBestEligibleNurse(timeSlot.availableNurses, preferredOffices);
    const bestRankedNurseID = bestNurse === null || bestNurse === void 0 ? void 0 : bestNurse.nurseID;
    return {
        ...timeSlot,
        preferredOffices,
        bestRankedNurseID,
    };
};
/**
 * Sets the data for the scheduling flow. Using the data fetched from the
 * `fetchDataWithRelationships` cloud function.
 */
const setDataForSchedulingFlow = (data, isPatient, patientPays) => {
    var _a;
    const store = getStore();
    const { localState } = store.state.schedulingFlow;
    const { bookings, order, patients, placeAccounts, placeGroups, prescriber, procedures, schedulingUser, supplyInstructions, supplyTransfers, } = data;
    const procedureIDs = getEligibleProcedureIDs(procedures, (_a = localState.proceduresIneligibleForRescheduling) !== null && _a !== void 0 ? _a : []);
    const proceduresToSet = objectFilter(procedures, (_, procedureID) => procedureIDs.includes(procedureID));
    const bookingIDs = Object.values(proceduresToSet).map((procedure) => procedure.bookingID);
    const bookingsToSet = objectFilter(bookings, (booking) => bookingIDs.some((bookingID) => bookingID === booking.id));
    const procedurePlaceAccountIDs = Object.values(proceduresToSet)
        .map((procedure) => { var _a; return Object.values((_a = procedure.placeAccounts) !== null && _a !== void 0 ? _a : {}); })
        .flat()
        .map((placeAccount) => placeAccount.id);
    const placeAccountsToSet = objectFilter(placeAccounts, (placeAccount) => procedurePlaceAccountIDs.some((placeAccountID) => placeAccountID === placeAccount.id));
    const placeGroupsToSet = objectFilter(placeGroups, (placeGroup) => Object.values(placeAccountsToSet).some((placeAccount) => placeAccount.placeGroupID === placeGroup.id));
    const patientIDs = Object.values(bookingsToSet).map((booking) => booking.patientID);
    const patientsToSet = objectFilter(patients, (patient) => patientIDs.some((patientID) => patientID === patient.id));
    const supplyInstructionsToSet = objectFilter(supplyInstructions, (_, supplyInstructionID) => Object.keys(proceduresToSet).some((procedureID) => procedureID === supplyInstructionID));
    store.commit('orders/SET_ORDERS', { [order.id]: order });
    store.commit('schedulingFlow/SET_BOOKINGS', bookingsToSet);
    store.commit('schedulingFlow/SET_PATIENTS', patientsToSet);
    store.commit('schedulingFlow/SET_PATIENT_PAYS', patientPays);
    store.commit('schedulingFlow/SET_PATIENT_SCHEDULES', isPatient);
    store.commit('schedulingFlow/SET_PLACE_ACCOUNTS', placeAccountsToSet);
    store.commit('schedulingFlow/SET_PLACE_GROUPS', placeGroupsToSet);
    store.commit('schedulingFlow/SET_PROCEDURES', proceduresToSet);
    store.commit('schedulingFlow/SET_SUPPLY_INSTRUCTION_CONFIG_OPTIONS', supplyInstructionsToSet);
    if (prescriber)
        store.commit('schedulingFlow/SET_PRESCRIBER', prescriber);
    if (supplyTransfers)
        store.commit('schedulingFlow/SET_SUPPLY_TRANSFERS', supplyTransfers);
    if (schedulingUser)
        store.commit('users/SET_STAFF_USER', schedulingUser);
};
/**
 * Starts the fetching of data for the visit details page as soon as a visit is
 * scheduled.
 */
const beginFetchingVisitDetailsData = (draftWaypointID) => {
    const store = getStore();
    store.dispatch('waypoint/getDataForVisitDetailsPage', draftWaypointID);
};

export { beginFetchingVisitDetailsData, getPatientPays, getStaffingInfoPerTimeSlot, getTextWithDryRun, getUpdatedTimeSlot, isSchedulingDryRun, setDataForSchedulingFlow };
